/**
 * @file billLineChoices.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-15
 * @modified 2026-10-28
 * @description A bill line and the PO line it bills, field by field: which
 *              side is right — chosen, never typed (Cam, 15 Oct 2026: "Adding
 *              a bill should be a situation where users can save typing by
 *              taking data from a PO and or a bill … on a per line and per
 *              field basis … no manual input where human error can creep in —
 *              just user choosing and checking info then saving").
 *
 *   THE FIELDS. For each PO line the bill bills (its bill lines grouped, as
 *   utils/billCostWriteBack groups them):
 *     quantity     the bill's (in the PO line's unit when billed in another,
 *                  utils/billUnitConversion) against the PO's — what was
 *                  SUPPLIED where recorded, else ordered;
 *     cost         the bill's unit price (converted likewise) against the PO
 *                  line's unit cost (and its order line's cost price);
 *     description  the bill line's words against the PO line's;
 *     item         the bill line's item code against the PO line's.
 *   And, when the bill is in another unit than the PO line, UNITS: "Use the
 *   PO's quantity and units".
 *
 *   THE DIRECTIONS. "Use the PO's" (`po`) puts the PO line's value on the
 *   BILL LINE (here, in the grid). "Use the bill's" (`bill`) puts the bill's
 *   value on the PO LINE — only through the paths that already exist and
 *   their rules: a quantity or description as a PO edit (`po_edits`,
 *   utils/addBillPoEdits), a cost through the cost write-back the user
 *   chooses (`po_cost_updates`, utils/billCostWriteBack — the order line's
 *   cost less additives, a Xero-made PO's cost then sent to Xero). A cost may
 *   also be kept different on purpose (`keep`). Every choice can be undone
 *   until the bill is saved.
 *
 *   WHAT GREYS A DIRECTION (the reason is shown, never hidden):
 *     both       a PO-H (it buys nothing — worked out from its order), a
 *                Direct Delivery haulage line (worked out from the order's
 *                rate), an archived / voided PO, a bill that cannot change its
 *                POs (in Xero already, a credit);
 *     po         several bill lines on one PO line (set each in the grid); a
 *                bill in another unit not yet put in the PO's (use the units
 *                choice first); nothing on the PO to take (no cost, no words,
 *                no item code);
 *     bill       quantity — a PO made in Xero or whose order is delivered /
 *                invoiced (utils/poEditScope: cost only, or cost and words); a
 *                split family's shared line; a supplied quantity recorded (the
 *                bill is checked against that, and ordered is not changed from
 *                a bill); a PO already billed by another bill (this one may be
 *                a part); description — a PO made in Xero (its words live in
 *                Xero); cost — nothing the write-back would change (no bill
 *                price); item — always (a PO line's item is changed on the PO
 *                or its order, never from a bill).
 *
 *   UNITS (Cam, 15 Oct 2026 — "can we allow users to accept the PO quantity
 *   and the maths that we have in place to work out what the bill's unit price
 *   means per the PO's units so they don't have to type it"). Offered only
 *   when the conversion is decided on evidence (billUnitConversion) and one
 *   bill line bills the PO line. The bill line becomes the PO line's quantity
 *   in the PO line's unit at amount ÷ quantity (4 dp — what Xero takes), its
 *   stored unit the PO's ('pack' / 't'), and its AMOUNT UNCHANGED: the
 *   supplier charged it, and Xero works a line out as quantity × price
 *   (Exclusive), so a price that cannot make that amount to the penny at 4 dp
 *   is not offered — the line keeps the bill's own units, greyed with the
 *   figures (convertToPoUnits). The figures as invoiced go with the line
 *   (`as_billed`, purchasing.bill_lines.as_billed — never sent to Xero) and
 *   are shown ("billed as 2,000.00 kg at £2.015"); the server checks them
 *   again against the PO line before saving (convertedLineProblem).
 *
 *   Why it matters for stock: a tracked item's bill line goes to Xero with its
 *   item code, so a line kept in kg adds kilograms to a stock counted in bags
 *   (bill 43866 had to be converted by hand for this).
 *
 *   1.0.1 (2026-10-16): the panel that showed these is retired — Add / Edit
 *   Bill now offers the PO's value and the bill's in each cell
 *   (utils/billLineFields, components/FieldChoice), and uses the units maths
 *   here (convertToPoUnits / convertLine / unconvertLine) and the audit's
 *   shape (lineChoicesForAudit). lineChoiceModel is kept for its tests.
 *
 *   1.1.0 (2026-10-28, Cam — a bill line whose cost differs from the order's
 *   or the contract's must have its cost chosen): lineChoicesForAudit keeps
 *   `price` — which figure the bill line's cost was taken from ('bill', 'po',
 *   'order', 'contract', 'typed'; utils/billCostCheck.PRICE_CHOICES).
 *
 *   Pure. The screen (Add / Edit Bill) and
 *   the server (billService: the converted-line check, the `_line_choices`
 *   audit) use it alike; utils/billLineChoices.js on the server is GENERATED
 *   from this file by scripts/sync-shared-utils.js — edit this one.
 */

import { roundHalfUp, formatQuantity, formatCount, formatDecimalInput } from './numberFormatters';
import { billUnitOf, billUnitConversion, conversionQtyText, conversionCostText } from './billUnitConversion';
import { unitLabelFor } from './units';
import { PRICE_CHOICES } from './billCostCheck';

export const CHOICE_FIELDS = Object.freeze(['quantity', 'cost', 'description', 'item']);
export const FIELD_LABELS = Object.freeze({
  units: 'Units', quantity: 'Quantity', cost: 'Unit cost', description: 'Description', item: 'Item',
});
export const DIRECTIONS = Object.freeze(['po', 'bill', 'keep']);

const EPS = 0.0001;
const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = parseFloat(String(v).replace(/[£,\s]/g, ''));
  return Number.isFinite(n) ? n : null;
};
const r2 = (n) => roundHalfUp(n, 2);
const r4 = (n) => roundHalfUp(n, 4);
const words = (s) => String(s ?? '').replace(/\s+/g, ' ').trim();
const sameWords = (a, b) => words(a).toLowerCase() === words(b).toLowerCase();
const codeOf = (s) => String(s ?? '').trim().toUpperCase();
const ok = () => ({ ok: true, reason: null });
const no = (reason) => ({ ok: false, reason });

/** "£50.375", "£2.015", "£400.00" — a unit price, 2 to 4 dp. */
export const money = (v) => `£${(r4(num(v) ?? 0) ?? 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
/** "£4,030.00" — an amount, to the penny. */
export const pounds = (v) => `£${(r2(num(v) ?? 0) ?? 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** The unit word a line counted in the PO line's unit is stored with ('pack' / 't'). */
export const poUnitWord = (conv) => (conv?.poUnit === 'tonnes' ? 't' : 'pack');
/** The unit the invoice billed in, as a stored word: 'kg' / 't' / 'pack'. */
const billUnitWord = (conv) => (conv?.billUnit === 'kg' ? 'kg' : conv?.billUnit === 'tonnes' ? 't' : 'pack');

/** "2,000.00 kg", "1.00 T", "48 × 25kg bag", "48" — a quantity in a stored unit word. */
export function quantityInUnit(qty, unit, kg = null) {
  const u = billUnitOf(unit);
  if (u === 'kg') return `${formatQuantity(qty)} kg`;
  if (u === 't') return `${formatQuantity(qty)} T`;
  if (u === 'pack') return kg > 0 && kg !== 1000 ? `${formatCount(qty)} × ${unitLabelFor(kg)}` : formatCount(qty);
  return formatQuantity(qty);
}

/** "a kg", "a tonne", "a 25kg bag", "each". */
function perUnitWord(unit, kg = null) {
  const u = billUnitOf(unit);
  if (u === 'kg') return 'a kg';
  if (u === 't') return 'a tonne';
  if (u === 'pack') return kg > 0 && kg !== 1000 ? `a ${unitLabelFor(kg)}` : 'each';
  return 'each';
}

/**
 * The figures as invoiced, in words: "Billed as 2,000.00 kg at £2.015 a kg
 * (£4,030.00)".
 * @param {{ quantity, unit_amount, unit, line_amount?, kg? }} asBilled
 */
export function asBilledText(asBilled) {
  if (!asBilled) return '';
  const amount = num(asBilled.line_amount) ?? r2((num(asBilled.quantity) || 0) * (num(asBilled.unit_amount) || 0));
  return `Billed as ${quantityInUnit(asBilled.quantity, asBilled.unit, asBilled.kg)} at ${money(asBilled.unit_amount)} ${perUnitWord(asBilled.unit, asBilled.kg)} (${pounds(amount)})`;
}

/**
 * A bill line put in the PO line's quantity and unit, its AMOUNT UNCHANGED.
 *
 * The quantity is the conversion's (billQty × ratio, 4 dp); the price the
 * amount over it at 4 dp — the nearest 4 dp price first, then the ones either
 * side — whichever makes the amount to the penny as Xero works it (quantity
 * × price, half-up). None does: not offered, with the figures (the line keeps
 * the bill's own units).
 *
 * @param {{ quantity: number|string, unitPrice: number|string, conv: object|null }} p
 * @returns {{ ok: true, quantity: number, unitPrice: number, amount: number, unit: 'pack'|'t',
 *   asBilled: { quantity, unit_amount, unit, line_amount, direction, kg } }
 *   | { ok: false, reason: string }}
 */
export function convertToPoUnits({ quantity, unitPrice, conv } = {}) {
  if (!conv) return no('The bill and the PO line are counted in the same unit — nothing to convert.');
  const q = num(quantity);
  const p = num(unitPrice);
  if (!(q > 0) || !(p > 0)) return no('The line needs a quantity and a price before it can be put in the PO\'s units.');
  const amount = r2(q * p);
  const Q = r4(q * conv.ratio);
  if (!(Q > 0)) return no('The PO\'s quantity for it comes to nothing.');
  const exact = amount / Q;
  const near = r4(exact);
  const candidates = [...new Set([near, r4(near - EPS), r4(near + EPS)])].filter(c => c > 0);
  const price = candidates.find(c => r2(Q * c) === amount);
  const poQty = quantityInUnit(Q, poUnitWord(conv), conv.kg);
  if (price === undefined) {
    return no(`${poQty} at ${money(near)} comes to ${pounds(r2(Q * near))}, not the ${pounds(amount)} billed — Xero works a line out as `
      + 'quantity × price (to 4 decimal places), so this line keeps the bill\'s own units.');
  }
  return {
    ok: true,
    quantity: Q,
    unitPrice: price,
    amount,
    unit: poUnitWord(conv),
    asBilled: {
      quantity: r4(q), unit_amount: r4(p), unit: billUnitWord(conv), line_amount: amount,
      direction: conv.direction, kg: conv.kg,
    },
  };
}

/** "2,000.00 kg at £2.015 → 80 × 25kg bag at £50.375; the line stays £4,030.00." */
export function unitsChoiceText(conv, take) {
  if (!conv) return '';
  if (!take?.ok) return `${conversionQtyText(conv)}; ${conversionCostText(conv)}.`;
  const from = quantityInUnit(take.asBilled.quantity, take.asBilled.unit, conv.kg);
  const to = quantityInUnit(take.quantity, take.unit, conv.kg);
  return `${from} at ${money(take.asBilled.unit_amount)} → ${to} at ${money(take.unitPrice)}; the line stays ${pounds(take.amount)}.`;
}

/**
 * Why a bill line saved in its PO line's units is refused, or null — the
 * server's own check before saving (and the screen's, to say so first).
 *
 * The line carries the figures as invoiced (`as_billed`). They must still be
 * read in the PO line's unit on evidence (billUnitConversion against the PO
 * line as it is now, after this save's PO edits), the line must be in that
 * unit, its quantity the converted one (or, chosen afterwards, the PO's
 * ordered / supplied), and its amount the one invoiced to the penny — or its
 * price the PO line's own cost (taken from the PO afterwards: the line then
 * no longer matches the invoice, which the screen says and the save's
 * "unallocated" check still catches).
 *
 * @param {object} p
 * @param {object} p.line — the bill line as sent (quantity, unit_amount, line_amount, unit, as_billed)
 * @param {object|null} p.poLine — its PO line (line_items element), as it now stands
 * @param {number|null} [p.itemWeight] — its item's unit_weight_kg
 * @param {number|null} [p.supplied] — what was supplied on it, where recorded
 * @param {string} [p.label] — how the line is named
 * @returns {string|null}
 */
export function convertedLineProblem({ line, poLine, itemWeight = null, supplied = null, label = 'A line' } = {}) {
  const as = line?.as_billed;
  if (!as) return null;
  const qB = num(as.quantity);
  const pB = num(as.unit_amount);
  if (!(qB > 0) || !(pB > 0)) return `${label} says it was put in its PO line's units, but the figures as billed are missing.`;
  if (!poLine) return `${label} says it was put in its PO line's units, but it is not on a PO line.`;
  const amount = r2(qB * pB);
  const conv = billUnitConversion({
    billQty: qB, billCost: pB, billAmount: amount, billUnit: as.unit ?? null,
    poCost: poLine.unitAmount, poQty: [poLine.quantity, supplied],
    lineWeight: poLine.unitWeightKg, itemWeight, poWords: poLine.description || null,
  });
  if (!conv) {
    return `${label}: ${asBilledText({ ...as, line_amount: amount })} no longer reads as the PO line's unit (its cost or quantity has changed) — put it back in the bill's units and check it.`;
  }
  const take = convertToPoUnits({ quantity: qB, unitPrice: pB, conv });
  if (!take.ok) return `${label}: ${take.reason}`;
  if (billUnitOf(line.unit) !== take.unit) return `${label} is in its PO line's units, but its unit says otherwise — put it back in the bill's units and choose again.`;
  const q = num(line.quantity);
  const p = num(line.unit_amount);
  const qtyOk = q !== null && [take.quantity, num(poLine.quantity), num(supplied)].some(x => x !== null && Math.abs(r4(q) - r4(x)) < EPS / 2);
  if (!qtyOk) return `${label}: ${formatQuantity(q)} is not ${quantityInUnit(take.quantity, take.unit, conv.kg)}, what was billed comes to in the PO's units.`;
  const lineAmount = num(line.line_amount);
  const asWorked = r2((q || 0) * (p || 0));
  const amountKept = asWorked === amount && (lineAmount === null || r2(lineAmount) === amount);
  const poPrice = num(poLine.unitAmount) > 0 && p !== null && r4(p) === r4(num(poLine.unitAmount));
  if (!amountKept && !poPrice) {
    return `${label}: ${formatQuantity(q)} at ${money(p)} comes to ${pounds(asWorked)}, not the ${pounds(amount)} billed — the amount must stay as invoiced.`;
  }
  if (lineAmount !== null && r2(lineAmount) !== asWorked) {
    return `${label}: its amount (${pounds(lineAmount)}) is not quantity × price (${pounds(asWorked)}).`;
  }
  return null;
}

/** The figures as invoiced, as stored (purchasing.bill_lines.as_billed), from a checked line. */
export function storedAsBilled(line) {
  const as = line?.as_billed;
  if (!as) return null;
  const qty = r4(num(as.quantity));
  const price = r4(num(as.unit_amount));
  return {
    quantity: qty, unit_amount: price, unit: billUnitOf(as.unit) || null,
    line_amount: r2((qty || 0) * (price || 0)),
    ...(as.direction ? { direction: String(as.direction).slice(0, 40) } : {}),
    ...(num(as.kg) > 0 ? { kg: num(as.kg) } : {}),
  };
}

/**
 * One PO line's fields, bill against PO, with which way each may go.
 *
 * @param {object} g
 * @param {object[]} g.lines — the grid's bill lines on this PO line (quantity, unit_price,
 *        product_name, product_code, bill_unit, _choices, _asBilled)
 * @param {{ purchase_order_number?, po_type?, status? }|null} g.po
 * @param {object|null} g.poLine — the PO's line_items element
 * @param {{ quantity, unitAmount, description }} g.eff — the PO line as it will be
 *        after this bill's PO edits (utils/billPoEdits.effectivePoLine)
 * @param {string} [g.poDescription] — the PO line's words without its additive names
 * @param {object|null} [g.scope] — utils/poEditScope's scope (null: anything may change)
 * @param {boolean} [g.editable] — may this bill change its POs at all (not in Xero, a bill)
 * @param {number|null} [g.supplied] — what was supplied on the PO line, where recorded
 * @param {boolean} [g.splitFamily] — a split family's shared line
 * @param {boolean} [g.billedElsewhere] — the PO is already billed by another bill
 * @param {object|null} [g.conversion] — billUnitConversion for these lines (none once converted)
 * @param {object|null} [g.costChange] — the poCostChanges entry for this PO line (or null)
 * @param {'bill'|'keep'|null} [g.costChoice] — the user's cost choice
 * @param {object} [g.poEdit] — this bill's PO edit of the line ({ quantity?, description? })
 * @param {boolean} [g.asBilledReady] — can a line's figures as invoiced be stored
 * @returns {{ blocked: string|null, units: object|null, fields: object[], drift: object|null, differs: number }}
 */
export function lineChoiceModel(g = {}) {
  const lines = Array.isArray(g.lines) ? g.lines : [];
  const single = lines.length === 1 ? lines[0] : null;
  const po = g.po || {};
  const li = g.poLine || null;
  const eff = g.eff || { quantity: num(li?.quantity) ?? 0, unitAmount: num(li?.unitAmount) ?? 0, description: li?.description || '' };
  const number = po.purchase_order_number || 'The PO';
  const st = String(po.status || '').toUpperCase();

  // Nothing either way.
  let blocked = null;
  if (po.po_type === 'haulage') blocked = `${number} is a PO-H: it buys nothing — its lines come from its customer order, so nothing is taken either way.`;
  else if (!li) blocked = 'This line is not on a line of the PO.';
  else if (li.kind === 'haulage') blocked = 'This is a haulage line, worked out from its order\'s haulage rate — nothing is taken either way.';
  else if (st === 'DELETED' || st === 'VOIDED') blocked = `${number} is ${st === 'DELETED' ? 'archived' : 'voided'}, so it is not changed and nothing is taken from it.`;
  const poBlock = blocked || (g.editable === false ? 'This bill cannot change its POs (it is in Xero already, or not a bill).' : null);
  const several = lines.length > 1 ? `${lines.length} bill lines bill this PO line — their total is compared; set each in the lines below.` : null;

  // Billed in another unit and not yet in the PO's: compared in the PO's unit.
  const conv = g.conversion || null;
  const unconverted = conv ? 'Billed in another unit — "Use the PO\'s quantity and units" puts it in the PO\'s first.' : null;

  // Bill figures, added up (several lines) and in the PO's unit.
  const billQtyRaw = r4(lines.reduce((s, l) => s + (num(l.quantity) || 0), 0));
  const billAmount = r2(lines.reduce((s, l) => s + r2((num(l.quantity) || 0) * (num(l.unit_price) || 0)), 0));
  const billCostRaw = single ? num(single.unit_price) : (billQtyRaw > 0 ? r4(billAmount / billQtyRaw) : null);
  const billQty = conv ? conv.quantity : billQtyRaw;
  const billCost = conv ? conv.cost : billCostRaw;
  const billUnit = single ? single.bill_unit : null;

  const fields = [];
  const pick = (take) => (blocked ? no(blocked) : take);

  // ── Quantity ──
  {
    const supplied = num(g.supplied);
    const basis = supplied !== null ? supplied : num(eff.quantity) ?? 0;
    const agrees = Math.abs((billQty || 0) - basis) < EPS;
    const poText = supplied !== null
      ? `${formatQuantity(eff.quantity)} ordered · ${formatQuantity(supplied)} supplied`
      : formatQuantity(eff.quantity);
    const billText = conv ? conversionQtyText(conv) : (single && billUnit ? quantityInUnit(billQtyRaw, billUnit) : formatQuantity(billQtyRaw));
    const takePo = pick(several ? no(several) : unconverted ? no(unconverted) : ok());
    let takeBill;
    if (poBlock) takeBill = no(poBlock);
    else if (g.scope) takeBill = no(g.scope.reason || 'Only this PO\'s cost changes from a bill.');
    else if (g.splitFamily) takeBill = no('This PO line is shared by split orders — change each order\'s quantity on the order itself.');
    else if (supplied !== null) takeBill = no(`What was supplied (${formatQuantity(supplied)}) is recorded — the bill is checked against it, so the PO's ordered quantity is not changed from the bill.`);
    else if (g.billedElsewhere) takeBill = no(`${number} is already billed by another bill — this one may be a part of it, so its quantity is not changed from here.`);
    else if (!(billQty > 0)) takeBill = no('The bill has no quantity to take.');
    else takeBill = ok();
    const chosen = agrees
      ? (single?._choices?.quantity === 'po' ? 'po' : (g.poEdit?.quantity !== undefined && g.poEdit?.quantity !== null && g.poEdit?.quantity !== '' ? 'bill' : null))
      : null;
    fields.push({
      field: 'quantity', label: FIELD_LABELS.quantity, poText, billText, agrees, chosen,
      poValue: basis, billValue: billQty,
      take: { po: agrees ? no('They agree.') : takePo, bill: agrees ? no('They agree.') : takeBill, keep: no('') },
    });
  }

  // ── Unit cost ── (the server's write-back rule decides what "differs")
  {
    const poCost = num(eff.unitAmount) ?? 0;
    const change = g.costChange || null;
    const olOnly = !!change?.order_line_only;
    const agrees = !change && billCost !== null && r4(billCost) === r4(poCost);
    const poText = poCost > 0 ? money(poCost) : 'None (£0)';
    const billText = conv ? conversionCostText(conv) : (billCost === null ? '—' : money(billCost));
    const olText = change && change.order_line_cost !== undefined
      ? `Order line ${change.order_line_cost === null ? 'not set' : money(change.order_line_cost)} → ${change.order_line_cost_new === null ? '—' : money(change.order_line_cost_new)}`
      : null;
    let takePo;
    if (olOnly) takePo = no('The PO already has the bill\'s cost — only its order line\'s cost differs.');
    else takePo = pick(several ? no(several) : unconverted ? no(unconverted) : !(poCost > 0) ? no('The PO line has no cost to take.') : ok());
    let takeBill;
    if (blocked) takeBill = no(blocked);
    else if (g.editable === false) takeBill = no(poBlock);
    else if (!change) takeBill = no(billCost > 0 ? 'Saving would not change the PO\'s cost.' : 'The bill has no price to take.');
    else takeBill = ok();
    const choice = g.costChoice || null;
    const chosen = choice === 'bill' || choice === 'keep'
      ? (change ? choice : null)
      : (agrees && single?._choices?.cost === 'po' ? 'po' : null);
    fields.push({
      field: 'cost', label: FIELD_LABELS.cost, poText, billText, agrees, chosen, note: olText,
      poValue: poCost, billValue: billCost,
      xeroNote: change?.xero_made ? 'Its new cost is sent to Xero after the bill is saved.' : null,
      take: {
        po: agrees ? no('They agree.') : takePo,
        bill: agrees ? no('They agree.') : takeBill,
        keep: agrees ? no('They agree.') : (change ? ok() : no('Nothing to keep — saving would not change the PO\'s cost.')),
      },
    });
  }

  // ── Description ──
  {
    const poWords = words(g.poDescription ?? eff.description ?? '');
    const billWords = single ? words(single.product_name) : '';
    const agrees = single ? sameWords(poWords, billWords) : true;
    const takePo = pick(several ? no(several) : !poWords ? no('The PO line has no words to take.') : ok());
    let takeBill;
    if (poBlock) takeBill = no(poBlock);
    else if (several) takeBill = no(several);
    else if (g.scope && g.scope.mode !== 'delivered') takeBill = no(g.scope.reason || 'Its words live in Xero.');
    else if (!billWords) takeBill = no('The bill line has no words to take.');
    else takeBill = ok();
    const chosen = agrees && single
      ? (single._choices?.description === 'po' ? 'po' : (g.poEdit?.description !== undefined && g.poEdit?.description !== null ? 'bill' : null))
      : null;
    fields.push({
      field: 'description', label: FIELD_LABELS.description, poText: poWords || '—', billText: several ? '(several lines)' : (billWords || '—'),
      agrees, chosen, poValue: poWords, billValue: billWords,
      take: { po: agrees ? no('They agree.') : takePo, bill: agrees ? no('They agree.') : takeBill, keep: no('') },
    });
  }

  // ── Item ──
  {
    const poCode = codeOf(li?.itemCode);
    const billCode = single ? codeOf(single.product_code) : '';
    const agrees = !single || !poCode || poCode === billCode;
    const takePo = pick(several ? no(several) : !poCode ? no('The PO line has no item.') : ok());
    const chosen = agrees && single?._choices?.item === 'po' ? 'po' : null;
    fields.push({
      field: 'item', label: FIELD_LABELS.item, poText: poCode || '—', billText: billCode || '—', agrees, chosen,
      poValue: poCode, billValue: billCode,
      take: {
        po: agrees ? no('They agree.') : takePo,
        bill: no('A PO line\'s item is changed on the PO or its customer order, not from a bill.'),
        keep: no(''),
      },
    });
  }

  // ── Units ──
  let units = null;
  if (single?._asBilled) {
    units = { applied: true, text: asBilledText(single._asBilled), take: blocked ? no(blocked) : ok() };
  } else if (conv) {
    const take = convertToPoUnits({ quantity: single?.quantity, unitPrice: single?.unit_price, conv });
    let gate = take.ok ? ok() : no(take.reason);
    if (blocked) gate = no(blocked);
    else if (several) gate = no(several);
    else if (g.editable === false) gate = no(poBlock);
    else if (take.ok && g.asBilledReady === false) gate = no('Not ready — the database update bill_line_as_billed.sql has not been run.');
    units = { applied: false, text: unitsChoiceText(conv, take), conversion: conv, convert: take.ok ? take : null, take: gate };
  }

  // Taking the PO's figures onto the bill moves the line away from the invoice: say so.
  let drift = null;
  if (single && single._before && single._before.amount !== undefined) {
    const now = r2((num(single.quantity) || 0) * (num(single.unit_price) || 0));
    const diff = r2(now - single._before.amount);
    if (Math.abs(diff) >= 0.005) {
      drift = { was: single._before.amount, now, diff,
        text: `The bill would no longer match the invoice: this line is now ${pounds(now)}, the invoice ${pounds(single._before.amount)} (${diff > 0 ? '+' : '−'}${pounds(Math.abs(diff))}).` };
    }
  }

  const differs = fields.filter(f => !f.agrees && !f.chosen).length + (units && !units.applied && units.take.ok ? 1 : 0);
  return { blocked, units, fields, drift, differs };
}

/** The values on the line before a choice took the PO's, so it can be undone. */
const BEFORE_KEYS = Object.freeze({
  quantity: ['quantity'],
  cost: ['unit_price'],
  description: ['product_name'],
  item: ['inventory_item_id', 'product_code'],
});

/**
 * "Use the PO's" for one field: the bill line takes `patch` (the PO's value,
 * as the grid holds it), remembering what it had — and what the line came to
 * on the invoice (`_before.amount`), so the screen can say if it no longer
 * matches.
 * @param {object} line — a grid line
 * @param {'quantity'|'cost'|'description'|'item'} field
 * @param {object} patch — e.g. { quantity: '80.00' }
 */
export function takeFromPo(line, field, patch) {
  if (!line || !BEFORE_KEYS[field]) return line;
  const before = { ...(line._before || {}) };
  if (before.amount === undefined && num(line.quantity) !== null && num(line.unit_price) !== null) {
    before.amount = r2(num(line.quantity) * num(line.unit_price));
  }
  if (!before[field]) before[field] = Object.fromEntries(BEFORE_KEYS[field].map(k => [k, line[k] ?? null]));
  return { ...line, ...patch, _before: before, _choices: { ...(line._choices || {}), [field]: 'po' } };
}

/** Undo "Use the PO's" for one field: the bill line's own value back. */
export function undoTakeFromPo(line, field) {
  const saved = line?._before?.[field];
  if (!line || !saved) return line;
  const before = { ...line._before };
  delete before[field];
  const choices = { ...(line._choices || {}) };
  delete choices[field];
  const left = Object.keys(before).filter(k => k !== 'amount' && k !== 'units');
  if (!left.length && !before.units) delete before.amount;
  return { ...line, ...saved, _before: before, _choices: choices };
}

/**
 * "Use the PO's quantity and units": the bill line in the PO line's quantity
 * and unit at the converted price, its amount unchanged (convertToPoUnits).
 * @param {object} line
 * @param {ReturnType<typeof convertToPoUnits>} take — ok
 */
export function convertLine(line, take) {
  if (!line || !take?.ok) return line;
  const before = { ...(line._before || {}) };
  before.units = { quantity: line.quantity ?? null, unit_price: line.unit_price ?? null, bill_unit: line.bill_unit ?? null };
  return {
    ...line,
    quantity: formatDecimalInput(take.quantity),
    unit_price: formatDecimalInput(take.unitPrice),
    bill_unit: take.unit,
    _asBilled: take.asBilled,
    _before: before,
    _choices: { ...(line._choices || {}), units: 'po' },
  };
}

/**
 * Undo the units choice: the bill's own quantity, price and unit back — and
 * the quantity / cost choices made after it, which were in the PO's units.
 */
export function unconvertLine(line) {
  if (!line?._asBilled) return line;
  const saved = line._before?.units || {
    quantity: formatDecimalInput(line._asBilled.quantity),
    unit_price: formatDecimalInput(line._asBilled.unit_amount),
    bill_unit: line._asBilled.unit || null,
  };
  const before = { ...(line._before || {}) };
  delete before.units;
  delete before.quantity;
  delete before.cost;
  const choices = { ...(line._choices || {}) };
  delete choices.units;
  delete choices.quantity;
  delete choices.cost;
  if (!before.description && !before.item) delete before.amount;
  return { ...line, ...saved, _asBilled: null, _before: before, _choices: choices };
}

/**
 * What was chosen, for the save's audit row (`line_choices` → `_line_choices`):
 * one entry per PO line with a choice in force, and the figures as invoiced
 * for a line put in the PO's units.
 * @param {Array<{ purchase_order_id, po_line_key, purchase_order_number?, description?, model }>} rows
 */
export function lineChoicesPayload(rows) {
  const out = [];
  for (const r of rows || []) {
    const choices = {};
    for (const f of r.model?.fields || []) if (f.chosen) choices[f.field] = f.chosen;
    if (r.model?.units?.applied) choices.units = 'po';
    if (!Object.keys(choices).length) continue;
    out.push({
      purchase_order_id: Number(r.purchase_order_id),
      po_line_key: String(r.po_line_key),
      ...(r.purchase_order_number ? { purchase_order_number: r.purchase_order_number } : {}),
      ...(r.description ? { description: String(r.description).slice(0, 200) } : {}),
      choices,
    });
  }
  return out;
}

/**
 * The request's `line_choices`, cleaned for the audit row, each with the
 * figures as invoiced of its converted line (from the checked lines).
 * @param {any} raw — the request's line_choices
 * @param {object[]} lines — the bill lines as sent (purchase_order_id, po_line_key, as_billed)
 * @returns {object[]|undefined}
 */
export function lineChoicesForAudit(raw, lines = []) {
  if (!Array.isArray(raw) || !raw.length) return undefined;
  const asBilledOf = new Map();
  for (const l of lines || []) {
    if (l?.as_billed && l.purchase_order_id && l.po_line_key) asBilledOf.set(`${Number(l.purchase_order_id)}|${l.po_line_key}`, storedAsBilled(l));
  }
  const out = [];
  for (const e of raw.slice(0, 500)) {
    if (!e || typeof e !== 'object') continue;
    const poId = parseInt(e.purchase_order_id, 10);
    const key = e.po_line_key ? String(e.po_line_key).slice(0, 120) : null;
    if (!Number.isInteger(poId) || !key) continue;
    const choices = {};
    for (const [f, d] of Object.entries(e.choices || {})) {
      if ((CHOICE_FIELDS.includes(f) || f === 'units') && DIRECTIONS.includes(d)) choices[f] = d;
      // Which figure a flagged line's cost was taken from (2026-10-28).
      else if (f === 'price' && PRICE_CHOICES.includes(d)) choices[f] = d;
    }
    if (!Object.keys(choices).length) continue;
    const as = asBilledOf.get(`${poId}|${key}`);
    out.push({
      purchase_order_id: poId, po_line_key: key,
      ...(e.purchase_order_number ? { purchase_order_number: String(e.purchase_order_number).slice(0, 40) } : {}),
      ...(e.description ? { description: String(e.description).slice(0, 200) } : {}),
      choices,
      ...(as ? { as_billed: as } : {}),
    });
  }
  return out.length ? out : undefined;
}
